import { selectedAsOfDateAtom, selectedPortfolioAtom, userPortfolioAtom } from '@bakerweb/client-state';
import { useRoutePermissions } from '@bakerweb/client-view-permissions';
import {
  BakerSoftwareSolutionsLogo,
  GlobalReportDateDropdown,
  MHidden,
  NavSection,
  Scrollbar
} from '@bakerweb/components';
import { useGetAsOfDates } from '@bakerweb/shared-queries-mutations-hooks';
import { useCollapseDrawer } from '@bakerweb/theme';
import {
  Box,
  CircularProgress,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useClientSidebarConfig } from './ClientSidebarConfig';

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex
    })
  }
}));

type DashboardSidebarProps = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
};

export function ClientDashboardSidebar({ isOpenSidebar, onCloseSidebar }: DashboardSidebarProps) {
  const { pathname } = useLocation();
  const portfolios = useAtomValue(userPortfolioAtom);
  const { isCollapse, collapseClick, collapseHover, onHoverEnter, onHoverLeave } = useCollapseDrawer();
  const [selectedPortfolio, setSelectedPortfolio] = useAtom(selectedPortfolioAtom);
  const [selectedAsOfDate, setSelectedAsOfDate] = useAtom(selectedAsOfDateAtom);
  const clientSidebarConfig = useClientSidebarConfig();
  const { loading: routePermissionsLoading } = useRoutePermissions();
  const { asOfDates, error: asOfDatesError } = useGetAsOfDates(useAtomValue(selectedPortfolioAtom));

  useEffect(() => {
    if (portfolios.length === 0) return;

    if (!selectedPortfolio || !portfolios.some((p) => p.portfolioId === selectedPortfolio?.portfolioId)) {
      setSelectedPortfolio(portfolios[0]);
    }

    if (asOfDates.length > 0 && !selectedAsOfDate && !asOfDates.some((a) => a.asOfDate === selectedAsOfDate)) {
      setSelectedAsOfDate(asOfDates[0].asOfDate);
    }
  }, [portfolios, asOfDates, selectedPortfolio, selectedAsOfDate, setSelectedPortfolio, setSelectedAsOfDate]);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname]);

  const handlePortfolioChange = (event: SelectChangeEvent<string>) => {
    const portfolioId = event.target.value;
    const portfolio = portfolios.find((p) => p.portfolioId === portfolioId);
    if (portfolio) {
      setSelectedPortfolio(portfolio);
      if (asOfDates.length > 0) {
        setSelectedAsOfDate(asOfDates[0].asOfDate);
      }
    }
  };

  const isLoading = portfolios.length === 0;

  if (asOfDatesError) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>Error loading report dates. Please refresh the page and try again.</Box>
    );
  }

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Stack
        spacing={3}
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center'
          })
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box component={RouterLink} to="/" sx={{ display: 'inline-flex' }}>
            <BakerSoftwareSolutionsLogo sx={{ width: 175, height: 90 }} />
          </Box>
        </Stack>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <Box>
            <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
              <InputLabel id="portfolio-select-label">Select Portfolio</InputLabel>
              <Select
                labelId="portfolio-select-label"
                value={selectedPortfolio?.portfolioId || ''}
                onChange={handlePortfolioChange}
                label="Select Portfolio"
              >
                {portfolios.map((permission) => (
                  <MenuItem key={permission.portfolioId} value={permission.portfolioId}>
                    {permission.portfolioCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <GlobalReportDateDropdown />
          </Box>
        )}
      </Stack>

      {routePermissionsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <NavSection navConfig={clientSidebarConfig} isShow={!isCollapse} />
      )}
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH
        },
        ...(collapseClick && {
          position: 'absolute'
        })
      }}
    >
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
              ...(isCollapse && {
                width: COLLAPSE_WIDTH
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
              })
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
